<template>
  <div class="fm-page">
    <!-- 顶部 -->
    <div class="fm-top">
      <div class="fm-top-l">
        <div class="fm-title">
          <el-button icon="ArrowLeft" text @click="back">返回</el-button>
          <h2>模板中心</h2>
          <span class="fm-badge">{{ pageTotal }} 套</span>
        </div>
        <p class="fm-sub">
          把做好的活动报名表沉淀成模板，任何城市都能一键套用。
          模板包含<b>字段结构 + 全部设置（规则 / 外观 / 推送）</b>，套出来的是全新草稿表单，模板自身不受影响。
        </p>
      </div>
      <div class="fm-top-r">
        <el-button type="primary" icon="Plus" @click="openCreate">从表单创建模板</el-button>
      </div>
    </div>

    <!-- 筛选 -->
    <div class="fm-bar">
      <el-input
          v-model="query.keyword"
          clearable
          placeholder="搜索 模板名称 / 说明"
          prefix-icon="Search"
          class="fm-keyword"
          @keyup.enter="search"></el-input>
      <span class="fm-bar-label">分类</span>
      <div class="fm-chips">
        <span class="fm-chip" :class="{ 'is-active': !query.category }" @click="pickCategory('')">全部</span>
        <span
            v-for="c in categories"
            :key="c"
            class="fm-chip"
            :class="{ 'is-active': query.category === c }"
            @click="pickCategory(c)">{{ c }}</span>
      </div>
      <el-select v-model="query.status" clearable placeholder="全部状态" class="fm-status">
        <el-option :value="1" label="已上架"/>
        <el-option :value="0" label="已下架"/>
      </el-select>
      <el-button icon="Search" @click="search">查询</el-button>
    </div>

    <!-- 模板网格 -->
    <div v-loading="loading" class="fm-grid">
      <div v-for="t in tableData" :key="t.id" class="fm-card" :class="{ 'is-off': t.status !== 1 }">
        <div class="fm-cover" @click="openPreview(t)">
          <img v-if="t.coverUrl" :src="t.coverUrl" alt=""/>
          <div v-else class="fm-cover--empty">
            <el-icon><Document /></el-icon>
          </div>
          <span class="fm-cover-tag">{{ t.fieldCount || 0 }} 个字段</span>
          <span v-if="t.status !== 1" class="fm-cover-off">已下架</span>
        </div>

        <div class="fm-body">
          <div class="fm-name" :title="t.name">{{ t.name }}</div>
          <div class="fm-meta">
            <span v-if="t.category" class="fm-cat">{{ t.category }}</span>
            <span>被套用 {{ t.useCount || 0 }} 次</span>
          </div>
          <div class="fm-desc" :title="t.description">{{ t.description || '暂无说明' }}</div>
        </div>

        <div class="fm-foot">
          <el-button size="small" type="primary" text @click="openUse(t)">
            <el-icon><MagicStick /></el-icon>套用
          </el-button>
          <el-button size="small" text @click="openPreview(t)">
            <el-icon><View /></el-icon>预览
          </el-button>
          <el-dropdown trigger="click" @command="(c) => onCmd(c, t)">
            <span class="fm-more">更多<el-icon><ArrowDown /></el-icon></span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="edit" icon="EditPen">编辑模板信息</el-dropdown-item>
                <el-dropdown-item :command="t.status === 1 ? 'off' : 'on'" icon="SwitchButton">
                  {{ t.status === 1 ? '下架' : '上架' }}
                </el-dropdown-item>
                <el-dropdown-item command="delete" icon="Delete" divided class="fm-del">删除模板</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>

      <div v-if="!loading && !tableData.length" class="fm-empty">
        <el-empty description="还没有模板 —— 把你做好的报名表沉淀成模板，之后一键套用">
          <el-button type="primary" icon="Plus" @click="openCreate">从表单创建模板</el-button>
          <el-button v-if="query.keyword || query.category || query.status !== null" @click="resetFilter">清除筛选</el-button>
        </el-empty>
      </div>
    </div>

    <div class="pagination">
      <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :page-sizes="[12, 24, 48]"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :total="pageTotal"
          @size-change="onSizeChange"
          @current-change="onPageChange"></el-pagination>
    </div>

    <!-- ============ 从表单创建 / 编辑信息 ============ -->
    <el-dialog
        v-model="createVisible"
        :title="createForm.id ? '编辑模板信息' : '从表单创建模板'"
        width="520px"
        align-center>
      <el-form label-width="86px">
        <el-form-item v-if="!createForm.id" label="源表单">
          <el-select
              v-model="createForm.formId"
              filterable
              clearable
              placeholder="选择要沉淀为模板的表单"
              class="fm-full">
            <el-option v-for="f in formOptions" :key="f.id" :label="f.title" :value="f.id">
              <span class="fm-opt-title">{{ f.title }}</span>
              <span class="fm-opt-code">{{ f.formKey }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="模板名">
          <el-input v-model="createForm.name" maxlength="100" placeholder="例如：暑期亲子跑报名模板"/>
        </el-form-item>
        <el-form-item label="分类">
          <el-select
              v-model="createForm.category"
              allow-create
              filterable
              clearable
              placeholder="报名 / 预约 / 调查 / 体测…"
              class="fm-full">
            <el-option v-for="c in categoryPool" :key="c" :label="c" :value="c"/>
          </el-select>
        </el-form-item>
        <el-form-item label="封面图">
          <el-input v-model="createForm.coverUrl" placeholder="图片地址（可留空）"/>
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="createForm.description" type="textarea" :rows="2" maxlength="300"/>
        </el-form-item>
      </el-form>
      <div class="fm-tip">
        <el-icon><InfoFilled /></el-icon>
        快照会带上字段结构、规则、外观与推送配置；短码与统计数据不会带入（每套出来的是全新表单）。
      </div>
      <template #footer>
        <el-button @click="createVisible = false">取 消</el-button>
        <el-button type="primary" @click="doCreate">{{ createForm.id ? '保 存' : '创 建 模 板' }}</el-button>
      </template>
    </el-dialog>

    <!-- ============ 套用模板 ============ -->
    <el-dialog v-model="useVisible" title="用模板创建表单" width="520px" align-center>
      <div class="fm-use-head">
        <div class="fm-use-name">{{ useTpl.name }}</div>
        <div class="fm-use-meta">
          <span v-if="useTpl.category" class="fm-cat">{{ useTpl.category }}</span>
          <span>{{ useTpl.fieldCount || 0 }} 个字段</span>
          <span>已被套用 {{ useTpl.useCount || 0 }} 次</span>
        </div>
      </div>
      <el-form label-width="86px">
        <el-form-item label="新表单标题">
          <el-input v-model="useForm.title" maxlength="100" :placeholder="useTpl.name"/>
        </el-form-item>
        <el-form-item label="放入分组">
          <el-select v-model="useForm.groupId" clearable placeholder="未分组" class="fm-full">
            <el-option :value="0" label="未分组"/>
            <el-option v-for="g in groups" :key="g.id" :value="g.id" :label="g.name"/>
          </el-select>
        </el-form-item>
      </el-form>
      <div class="fm-tip">
        <el-icon><InfoFilled /></el-icon>
        套出来的是<b>草稿</b>表单 —— 请先确认标题、适用城市与起止时间再发布。
      </div>
      <template #footer>
        <el-button @click="useVisible = false">取 消</el-button>
        <el-button type="primary" @click="doUse">创 建 并 去 设 计</el-button>
      </template>
    </el-dialog>

    <!-- ============ 预览 ============ -->
    <el-dialog v-model="previewVisible" :title="previewTpl.name || '模板预览'" width="620px" align-center>
      <div v-loading="previewLoading" class="fm-pv">
        <div class="fm-pv-head">
          <span v-if="previewTpl.category" class="fm-cat">{{ previewTpl.category }}</span>
          <span>{{ (previewSchema || []).length }} 个字段</span>
        </div>
        <div v-if="previewSchema && previewSchema.length" class="fm-pv-list">
          <div v-for="(f, i) in previewSchema" :key="f._uid || f.key || i" class="fm-pv-item">
            <span class="fm-pv-no">{{ i + 1 }}</span>
            <span class="fm-pv-label">{{ f.label || f.key || '未命名字段' }}</span>
            <span class="fm-pv-type">{{ typeText(f.type) }}</span>
            <span v-if="f.required" class="fm-pv-req">必填</span>
          </div>
        </div>
        <el-empty v-else description="该模板没有字段结构" :image-size="70"/>
      </div>
      <template #footer>
        <el-button @click="previewVisible = false">关 闭</el-button>
        <el-button type="primary" @click="previewVisible = false; openUse(previewTpl)">套 用 此 模 板</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  formTemplatePage,
  formTemplateCategories,
  formTemplateDetail,
  formTemplateSave,
  formTemplateSaveAs,
  formTemplateDelete,
  formTemplateStatus,
  formTemplateUse,
  formGroupList,
  formPage,
} from '@/api/form';

// 字段类型中文名（与设计器的 27 种类型对齐，预览里给人看）
const TYPE_TEXT = {
  input: '单行文本', textarea: '多行文本', number: '数字', idcard: '身份证',
  phone: '手机号', school: '学校', date: '日期', time: '时间', region: '地区',
  location: '定位', radio: '单选', checkbox: '多选', select: '下拉', matrix: '矩阵',
  city: '城市', campus: '校区', grade: '年级', project: '项目', image: '图片',
  switch: '开关', rate: '评分', slider: '滑块', imgs: '图片组', tips: '提示文字',
  section: '分组标题', divider: '分割线', signature: '签名',
};

const DEFAULT_CREATE = () => ({
  id: null, formId: null, name: '', category: '', coverUrl: '', description: '',
});

export default {
  name: 'FormTemplate',
  data() {
    return {
      loading: false,
      tableData: [],
      pageTotal: 0,
      categories: [],
      categoryPool: ['报名', '预约', '调查', '体测', '家长会', '其它'],
      groups: [],
      formOptions: [],
      query: {
        pageIndex: 1,
        pageSize: 12,
        keyword: '',
        category: '',
        status: null,
      },
      // 创建 / 编辑
      createVisible: false,
      createForm: DEFAULT_CREATE(),
      // 套用
      useVisible: false,
      useTpl: {},
      useForm: { title: '', groupId: null },
      // 预览
      previewVisible: false,
      previewLoading: false,
      previewTpl: {},
      previewSchema: [],
    };
  },
  methods: {
    typeText(t) {
      return TYPE_TEXT[t] || t || '未知';
    },
    back() {
      const p = this.$router.push({ path: '/formList' });
      if (p && typeof p.catch === 'function') {
        p.catch(err => this.$message.error(`返回失败：${(err && err.message) || '未知错误'}`));
      }
    },
    /* ---------- 数据 ---------- */
    load() {
      this.loading = true;
      formTemplatePage(this.query).then(val => {
        this.loading = false;
        if (val && val.code === 200) {
          this.tableData = (val.data && val.data.records) || [];
          this.pageTotal = (val.data && val.data.total) || 0;
        } else {
          this.$message.warning((val && val.message) || '获取模板失败');
        }
      }).catch(() => {
        this.loading = false;
      });
    },
    loadCategories() {
      formTemplateCategories().then(val => {
        if (val && val.code === 200 && Array.isArray(val.data)) {
          this.categories = val.data;
        }
      });
    },
    loadGroups() {
      formGroupList().then(val => {
        if (val && val.code === 200 && Array.isArray(val.data)) {
          this.groups = val.data;
        }
      });
    },
    loadForms() {
      formPage({ pageIndex: 1, pageSize: 500 }).then(val => {
        if (val && val.code === 200) {
          this.formOptions = (val.data && val.data.records) || [];
        }
      });
    },
    search() {
      this.query.pageIndex = 1;
      this.load();
    },
    /** 空态出口：把关键字 / 分类 / 状态一起清掉再看全部 */
    resetFilter() {
      this.query.keyword = '';
      this.query.category = '';
      this.query.status = null;
      this.query.pageIndex = 1;
      this.load();
    },
    pickCategory(c) {
      this.query.category = c;
      this.query.pageIndex = 1;
      this.load();
    },
    onSizeChange(size) {
      this.query.pageSize = size;
      this.query.pageIndex = 1;
      this.load();
    },
    onPageChange(index) {
      this.query.pageIndex = index;
      this.load();
    },
    /* ---------- 创建 / 编辑 ---------- */
    openCreate() {
      this.createForm = DEFAULT_CREATE();
      this.createVisible = true;
      if (!this.formOptions.length) this.loadForms();
    },
    doCreate() {
      if (this.createForm.id) {
        this.doSaveMeta();
        return;
      }
      if (!this.createForm.formId) {
        this.$message.warning('请先选择源表单');
        return;
      }
      formTemplateSaveAs({
        formId: this.createForm.formId,
        name: this.createForm.name,
        category: this.createForm.category,
        coverUrl: this.createForm.coverUrl,
        description: this.createForm.description,
      }).then(val => {
        if (val && val.code === 200) {
          this.$message.success('模板已创建');
          this.createVisible = false;
          this.load();
          this.loadCategories();
        } else {
          this.$message.warning((val && val.message) || '创建失败');
        }
      });
    },
    openEdit(t) {
      this.createForm = {
        id: t.id,
        formId: null,
        name: t.name,
        category: t.category || '',
        coverUrl: t.coverUrl || '',
        description: t.description || '',
      };
      this.createVisible = true;
    },
    doSaveMeta() {
      if (!this.createForm.name || !String(this.createForm.name).trim()) {
        this.$message.warning('请填写模板名');
        return;
      }
      formTemplateSave({
        id: this.createForm.id,
        name: String(this.createForm.name).trim(),
        category: this.createForm.category || null,
        coverUrl: this.createForm.coverUrl || null,
        description: this.createForm.description || null,
      }).then(val => {
        if (val && val.code === 200) {
          this.$message.success('已保存');
          this.createVisible = false;
          this.load();
          this.loadCategories();
        } else {
          this.$message.warning((val && val.message) || '保存失败');
        }
      });
    },
    /* ---------- 更多 ---------- */
    onCmd(cmd, t) {
      if (cmd === 'edit') {
        this.openEdit(t);
        return;
      }
      if (cmd === 'on' || cmd === 'off') {
        const next = cmd === 'on' ? 1 : 0;
        formTemplateStatus(t.id, next).then(val => {
          if (val && val.code === 200) {
            this.$message.success(next === 1 ? '已上架' : '已下架');
            this.load();
          } else {
            this.$message.warning((val && val.message) || '操作失败');
          }
        });
        return;
      }
      if (cmd === 'delete') {
        this.$confirm(
            `确定删除模板「${t.name}」吗？\n已用该模板创建出的表单不受影响。`,
            '提示',
            { type: 'warning' },
        ).then(() => {
          formTemplateDelete(t.id).then(val => {
            if (val && val.code === 200) {
              this.$message.success('已删除');
              this.load();
              this.loadCategories();
            } else {
              this.$message.warning((val && val.message) || '删除失败');
            }
          });
        }).catch(() => {});
      }
    },
    /* ---------- 套用 ---------- */
    openUse(t) {
      this.useTpl = t;
      this.useForm = { title: t.name || '', groupId: null };
      this.useVisible = true;
      if (!this.groups.length) this.loadGroups();
    },
    doUse() {
      formTemplateUse(this.useTpl.id, this.useForm.title, this.useForm.groupId).then(val => {
        if (val && val.code === 200 && val.data && val.data.formId) {
          this.$message.success(val.message || '已创建表单');
          this.useVisible = false;
          // 直接进设计器，省掉「回列表 → 再找刚建的草稿」两步
          this.goDesigner(val.data.formId);
        } else {
          this.$message.warning((val && val.message) || '创建失败');
        }
      });
    },
    goDesigner(id) {
      const p = this.$router.push({ path: '/formDesigner', query: { id } });
      if (p && typeof p.catch === 'function') {
        p.catch(err => this.$message.error(`打开设计器失败：${(err && err.message) || '未知错误'}`));
      }
    },
    /* ---------- 预览 ---------- */
    openPreview(t) {
      this.previewTpl = t;
      this.previewSchema = [];
      this.previewVisible = true;
      this.previewLoading = true;
      formTemplateDetail(t.id).then(val => {
        this.previewLoading = false;
        if (val && val.code === 200 && val.data && val.data.ok) {
          this.previewSchema = val.data.schema || [];
          // 详情接口会带回完整模板（含 schema/settings），顺手补全封面等信息
          if (val.data.template) this.previewTpl = val.data.template;
        } else {
          this.$message.warning((val && val.data && val.data.message) || '模板详情获取失败');
        }
      }).catch(() => {
        this.previewLoading = false;
      });
    },
  },
  created() {
    this.load();
    this.loadCategories();
    this.loadGroups();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在 9 个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则 9 份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单 9 页」负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.fm-page {
  /* —— Element 主色对齐「焱火绿」——
     此前后台从未覆盖 --el-color-primary，Element 一直是默认**蓝色**，
     而页面自定义元素用 #8dc63f ⇒ 同屏「按钮蓝 + 品牌绿」两个颜色。
     这 7 个变量一改，本页所有 el-button / el-switch / el-radio / el-checkbox /
     el-slider / el-pagination / focus 描边… 一次性全部变绿。
     light-3/5/7/8/9 与 dark-2 必须一并给出，否则 hover / disabled 态会
     回退到 Element 基于**蓝色**算出的浅色，出现「绿底 + 蓝边」的跳变。 */
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶；置成令牌后同类文字不会再各写一个灰） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */
.fm-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

.fm-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 14px 20px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fm-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fm-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fm-badge {
  padding: 1px 9px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.fm-sub {
  margin: 6px 0 0;
  max-width: 780px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.fm-sub b {
  color: var(--yh-t2);
}

.fm-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.fm-keyword {
  width: 220px;
}

.fm-bar-label {
  font-size: 13px;
  color: var(--yh-t2);
}

.fm-status {
  width: 118px;
  margin-left: auto;
}

.fm-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.fm-chip {
  padding: 3px 12px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-bg);
  color: var(--yh-t2);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.fm-chip:hover {
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
}

.fm-chip.is-active {
  background: var(--yh-theme);
  color: #fff;
}

/* ---------- 卡片 ---------- */
.fm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(258px, 1fr));
  gap: 14px;
  min-height: 150px;
}

.fm-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: var(--yh-r-lg);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
  transition: all 0.18s;
}

.fm-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 21, 0.1);
}

.fm-card.is-off {
  opacity: 0.72;
}

.fm-cover {
  position: relative;
  height: 122px;
  background: var(--yh-bg);
  cursor: pointer;
  overflow: hidden;
}

.fm-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.fm-cover--empty {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: #dcdfe6;
}

.fm-cover-tag {
  position: absolute;
  left: 8px;
  bottom: 8px;
  padding: 2px 8px;
  border-radius: var(--yh-r-pill);
  background: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  color: var(--yh-t2);
}

.fm-cover-off {
  position: absolute;
  right: 8px;
  top: 8px;
  padding: 2px 8px;
  border-radius: var(--yh-r-pill);
  background: rgba(144, 147, 153, 0.92);
  font-size: 12px;
  color: #fff;
}

.fm-body {
  flex: 1 1 auto;
  padding: 10px 12px 6px;
}

.fm-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--yh-t1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fm-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  font-size: 12px;
  color: var(--yh-t3);
}

.fm-cat {
  padding: 1px 7px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
}

.fm-desc {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.6;
  color: var(--yh-t4);
  height: 34px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.fm-foot {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px 8px;
  border-top: 1px solid #f2f4f6;
}

.fm-more {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  margin-left: 6px;
  font-size: 13px;
  color: var(--yh-t2);
  cursor: pointer;
  outline: none;
}

.fm-more:hover {
  color: var(--yh-theme);
}

.fm-del {
  color: #f56c6c;
}

.fm-empty {
  grid-column: 1 / -1;
}

.pagination {
  margin-top: 16px;
  text-align: right;
}

/* ---------- 弹窗 ---------- */
.fm-full {
  width: 100%;
}

.fm-tip {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 2px;
  padding: 9px 12px;
  border-radius: var(--yh-r);
  background: var(--yh-theme-ll);
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t2);
}

.fm-tip b {
  color: var(--yh-theme-dd);
}

.fm-opt-title {
  margin-right: 8px;
}

.fm-opt-code {
  font-family: Consolas, Monaco, monospace;
  font-size: 11px;
  color: var(--yh-t5);
}

.fm-use-head {
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: var(--yh-r);
  background: var(--yh-fill);
}

.fm-use-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--yh-t1);
}

.fm-use-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  font-size: 12px;
  color: var(--yh-t3);
}

/* 预览 */
.fm-pv {
  min-height: 140px;
  max-height: 56vh;
  overflow-y: auto;
}

.fm-pv-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--yh-t3);
}

.fm-pv-list {
  border: 1px solid var(--yh-line);
  border-radius: var(--yh-r);
  overflow: hidden;
}

.fm-pv-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  font-size: 13px;
  color: var(--yh-t2);
  border-bottom: 1px solid #f7f8fa;
}

.fm-pv-item:last-child {
  border-bottom: none;
}

.fm-pv-item:nth-child(odd) {
  background: var(--yh-fill);
}

.fm-pv-no {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #f0f1f3;
  color: var(--yh-t3);
  font-size: 11px;
  line-height: 20px;
  text-align: center;
}

.fm-pv-label {
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.fm-pv-type {
  flex: 0 0 auto;
  padding: 1px 7px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-bg);
  color: var(--yh-t3);
  font-size: 12px;
}

.fm-pv-req {
  flex: 0 0 auto;
  color: #f56c6c;
  font-size: 12px;
}
</style>
